/**
 * La chaîne d'opérations d'une variable — `$VILLE.upcase`, `$PRIX.devise`, `$REF.split("-")[0]`,
 * `$NB.pluriel("pièce")` — côté client : le miroir de StringInterpolator::Chain et de son
 * registre (Ruby), pour que l'éditeur montre ce que le serveur rendra. Même grammaire
 * (CHAIN_SOURCE = Chain::SOURCE), même registre (OPERATIONS), mêmes cas
 * (test/fixtures/files/interpolation_cases.json). Documenté dans docs/reference/interpolation.md.
 *
 * Une valeur circule en texte ou en tableau (`.split` → tableau, `.join` → texte, `[n]` →
 * élément, `[a..b]` → tranche) ; `chained` recolle un tableau par des espaces. Une opération
 * inconnue, ou mal appelée, vide la valeur.
 *
 * Ce que le client ne sait pas : `.departement` (la table des départements vit au serveur) —
 * la valeur passe inchangée.
 */
export type ChainValue = string | string[];

/** Un ou deux arguments entre guillemets : ("x"), ("x", "y"). */
const ARGS = String.raw`\("[^"]*"(?:,\s*"[^"]*")?\)`;
/** Une opération : .nom, .nom(args), [n], [a..b], [a..], [..b] — la grammaire de Chain::SOURCE. */
export const CHAIN_SOURCE = String.raw`(?:\.\w+(?:${ARGS})?|\[-?\d+(?:\.\.-?\d+|\.\.)?\]|\[\.\.(?:-?\d+)\])`;
/** Un token et sa chaîne : `$NOM` puis `.upcase`, `[0..2]`… — groupes (nom, chaîne). */
export const VARIABLE_SOURCE = String.raw`\$(\w+)(${CHAIN_SOURCE}*)`;
/** La même grammaire, en groupes : nom et arguments d'une méthode, bornes d'un index ou d'une tranche. */
const OPERATION = /\.(\w+)(?:\("([^"]*)"(?:,\s*"([^"]*)")?\))?|\[(-?\d+)?(\.\.)?(-?\d+)?\]/g;

/**
 * Le scope d'un binding, déduit de ses tokens : `subject` dès qu'un token est du contrat du
 * sujet (l'expansion le liera, le relais re-scope le reste), `company` sinon.
 */
export function bindingScope(expression: string, subjectTokens: string[]): "subject" | "company" {
  const subject = new Set(subjectTokens.map((token) => token.toUpperCase()));
  const tokens = (expression.match(/\$(\w+)/g) || []).map((token) => token.slice(1).toUpperCase());
  return tokens.some((token) => subject.has(token)) ? "subject" : "company";
}

/** La valeur brute d'une variable : le nom ne dépend pas de la casse. */
export function lookup(values: Record<string, unknown>, name: string): unknown {
  return values[name] ?? values[name.toUpperCase()] ?? values[name.toLowerCase()];
}

export interface Operation {
  name: string;
  args: (string | number | null)[];
}

/** Les opérations d'une chaîne, dans l'ordre : `[n]` est `at`, `[a..b]` est `slice`. */
export function parseChain(chain: string): Operation[] {
  return [...chain.matchAll(OPERATION)].map(([, name, arg1, arg2, from, dots, to]) => {
    if (name) return { name, args: [arg1, arg2].filter((arg) => arg !== undefined) };
    if (!dots) return { name: "at", args: [Number(from)] };
    return { name: "slice", args: [from === undefined ? null : Number(from), to === undefined ? null : Number(to)] };
  });
}

/* ---------- le registre ---------- */

interface Definition {
  /** Le nombre d'arguments accepté. */
  arity: [number, number];
  /** Appliquée à chaque élément d'un tableau (sinon à la valeur entière). */
  each: boolean;
  call: (value: ChainValue, ...args: (string | number | null)[]) => ChainValue;
}

const each = (arity: [number, number], fn: (value: string, ...args: string[]) => string): Definition =>
  ({ arity, each: true, call: (value, ...args) => fn(String(value), ...(args as string[])) });
const whole = (arity: [number, number], fn: Definition["call"]): Definition => ({ arity, each: false, call: fn });

const OPERATIONS: Record<string, Definition> = {
  // Le texte
  upcase: each([0, 0], (v) => v.toUpperCase()),
  downcase: each([0, 0], (v) => v.toLowerCase()),
  humanize: each([0, 0], humanize),
  replace: each([2, 2], (v, from, to) => v.split(from).join(to)),
  parameterize: each([0, 1], (v, sep) => parameterize(v, sep ?? "-")),
  // Tableaux et tranches
  split: whole([1, 1], (v, sep) => String(v).split(separator(String(sep)))),
  join: whole([1, 1], (v, sep) => (Array.isArray(v) ? v.join(String(sep)) : String(v))),
  at: whole([1, 1], (v, index) => String(at(v, Number(index)) ?? "")),
  slice: whole([2, 2], (v, from, to) => slice(v, from as number | null, to as number | null)),
  // Les mises en forme
  milliers: each([0, 0], thousands),
  devise: each([0, 0], currency),
  telephone: each([0, 0], phone),
  pluriel: each([1, 2], (v, singular, pluralForm) => plural(v, singular, pluralForm ?? `${singular}s`)),
  date: each([0, 1], (v, format) => date(v, format ?? "long")),
  departement: each([0, 0], (v) => v),
};

/** Applique une opération ; null pour une opération inconnue ou mal appelée. */
function applyOperation(value: ChainValue, { name, args }: Operation): ChainValue | null {
  const definition = OPERATIONS[name];
  if (!definition || args.length < definition.arity[0] || args.length > definition.arity[1]) return null;
  if (definition.each && Array.isArray(value)) return value.map((item) => definition.call(item, ...args) as string);
  return definition.call(value, ...args);
}

export function applyChain(value: string, chain: string): ChainValue | null {
  let current: ChainValue | null = value;
  for (const operation of parseChain(chain)) {
    if (current === null) return null;
    current = applyOperation(current, operation);
  }
  return current;
}

/** Une chaîne appliquée et recollée : ce qu'un token rend dans un texte. */
export function chained(value: string, chain: string): string {
  return finalize(applyChain(value, chain));
}

export function finalize(value: ChainValue | null): string {
  if (value === null) return "";
  return Array.isArray(value) ? value.join(" ") : value;
}

/* ---------- les mises en forme (StringInterpolator::Formats) ---------- */

const NBSP = " ";

/** `.split("[,;]")` : entre crochets, un motif ; sinon, le texte tel quel. */
function separator(sep: string): string | RegExp {
  return /^\[.*\]$/.test(sep) ? new RegExp(sep) : sep;
}

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre",
  "novembre", "décembre"];
const MONTHS_ABBR = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const DATE_FORMATS: Record<string, string> = { long: "%-d %B %Y", court: "%d/%m/%Y", mois: "%B %Y" };
const pad2 = (n: number): string => String(n).padStart(2, "0");

/** Une date lisible — l'ISO ou le jj/mm/aaaa, au format nommé ou au motif strftime ; une
 *  valeur illisible reste telle quelle. */
function date(value: string, format: string): string {
  const text = value.trim();
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ].*)?$/.exec(text);
  const fr = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  const [year, month, day] = iso ? [iso[1], iso[2], iso[3]].map(Number) : fr ? [fr[3], fr[2], fr[1]].map(Number) : [];
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return value;

  return (DATE_FORMATS[format] ?? format).replace(/%(-?)([deYmBby%])/g, (_match, flag: string, code: string) => {
    switch (code) {
      case "d": return flag === "-" ? String(day) : pad2(day);
      case "e": return String(day);
      case "m": return flag === "-" ? String(month) : pad2(month);
      case "B": return MONTHS[month - 1];
      case "b": return MONTHS_ABBR[month - 1];
      case "Y": return String(year);
      case "y": return pad2(year % 100);
      default: return "%";
    }
  });
}

/** Une valeur d'enum lisible (le `humanize` de Rails) : soulignés en espaces, première lettre
 *  en capitale, le reste en minuscules. */
function humanize(value: string): string {
  const text = value.replace(/_/g, " ").trim().toLowerCase();
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}

/** Le séparateur de milliers à la française, en espace insécable — un entier seulement. */
function thousands(value: string): string {
  return /^-?\d+$/.test(value) ? value.replace(/(\d)(?=(\d{3})+$)/g, `$1${NBSP}`) : value;
}

/** Un montant : les milliers groupés et le symbole, insécable — "250 000 €". */
function currency(value: string): string {
  return value.trim() === "" ? "" : `${thousands(value)}${NBSP}€`;
}

/** Un numéro français en paires insécables ; un +33 garde sa tête ; le reste passe tel quel. */
function phone(value: string): string {
  const digits = value.replace(/[\s.()-]/g, "");
  if (/^0\d{9}$/.test(digits)) return digits.match(/\d{2}/g)!.join(NBSP);
  if (/^\+33\d{9}$/.test(digits)) return `+33${NBSP}${digits[3]}${NBSP}${digits.slice(4).match(/\d{2}/g)!.join(NBSP)}`;
  return value;
}

/** Le nombre et son nom accordé — "3 pièces", "1 pièce" ; une valeur non numérique passe telle quelle. */
function plural(value: string, singular: string, pluralForm: string): string {
  const number = Number(value.replace(",", ".").replace(/[\s ]/g, ""));
  if (value.trim() === "" || Number.isNaN(number)) return value;
  return `${value}${NBSP}${Math.abs(number) >= 2 ? pluralForm : singular}`;
}

function parameterize(value: string, sep: string): string {
  const slug = value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, sep);
  return sep ? slug.replace(new RegExp(`^${escape(sep)}+|${escape(sep)}+$`, "g"), "") : slug;
}

function escape(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function at(value: ChainValue, index: number): string | undefined {
  const list = Array.isArray(value) ? value : [...value];
  return list.at(index);
}

/** `value[from..to]`, bornes incluses comme en Ruby (négatives depuis la fin, ouvertes à null). */
function slice(value: ChainValue, from: number | null, to: number | null): ChainValue {
  const list = Array.isArray(value) ? value : [...value];
  const start = from === null ? 0 : from < 0 ? Math.max(list.length + from, 0) : from;
  const end = to === null ? list.length : to < 0 ? list.length + to + 1 : to + 1;
  const out = start > list.length ? [] : list.slice(start, Math.max(end, start));
  return Array.isArray(value) ? out : out.join("");
}
